import { useEffect, useRef, useCallback } from 'react';

// Uses Leaflet loaded via CDN (window.L)
// Draws a rectangle on OSM tiles; calls onBBoxChange(bbox) when area selected

export default function WorldGenMap({ bbox, onBBoxChange, sizeKm = 1 }) {
  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const rectLayer = useRef(null);
  const drawingRef = useRef(false);
  const startLatLng = useRef(null);

  useEffect(() => {
    if (!window.L || leafletMap.current) return;

    const map = window.L.map(mapRef.current, {
      center: [51.1657, 10.4515],
      zoom: 6,
    });

    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    leafletMap.current = map;

    // Draw mode: mousedown → mousemove → mouseup
    map.on('mousedown', (e) => {
      if (!e.originalEvent.shiftKey) return;
      e.originalEvent.preventDefault();
      drawingRef.current = true;
      startLatLng.current = e.latlng;
      map.dragging.disable();
    });

    map.on('mousemove', (e) => {
      if (!drawingRef.current || !startLatLng.current) return;
      if (rectLayer.current) map.removeLayer(rectLayer.current);
      const bounds = window.L.latLngBounds(startLatLng.current, e.latlng);
      rectLayer.current = window.L.rectangle(bounds, {
        color: '#f97316', weight: 2, fillOpacity: 0.15,
      }).addTo(map);
    });

    map.on('mouseup', (e) => {
      if (!drawingRef.current) return;
      drawingRef.current = false;
      map.dragging.enable();
      if (!startLatLng.current) return;
      const b = window.L.latLngBounds(startLatLng.current, e.latlng);
      const newBbox = {
        minLon: Math.min(b.getWest(), b.getEast()),
        minLat: Math.min(b.getSouth(), b.getNorth()),
        maxLon: Math.max(b.getWest(), b.getEast()),
        maxLat: Math.max(b.getSouth(), b.getNorth()),
      };
      if (Math.abs(newBbox.maxLon - newBbox.minLon) > 0.0001 &&
          Math.abs(newBbox.maxLat - newBbox.minLat) > 0.0001) {
        onBBoxChange(newBbox);
      }
      startLatLng.current = null;
    });

    return () => { if (leafletMap.current) { leafletMap.current.remove(); leafletMap.current = null; } };
  }, []); // eslint-disable-line

  // Sync external bbox → map rect
  useEffect(() => {
    if (!leafletMap.current || !bbox || !window.L) return;
    if (rectLayer.current) leafletMap.current.removeLayer(rectLayer.current);
    const bounds = window.L.latLngBounds(
      [bbox.minLat, bbox.minLon], [bbox.maxLat, bbox.maxLon]
    );
    rectLayer.current = window.L.rectangle(bounds, {
      color: '#f97316', weight: 2, fillOpacity: 0.15,
    }).addTo(leafletMap.current);
    leafletMap.current.fitBounds(bounds, { padding: [40, 40] });
  }, [bbox]);

  const handleSearch = useCallback(async (e) => {
    e.preventDefault();
    const q = e.target.elements.q.value.trim();
    if (!q) return;
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1`,
        { headers: { 'Accept-Language': 'de' } }
      );
      const data = await res.json();
      if (data[0]) {
        const { lat, lon } = data[0];
        const centerLat = parseFloat(lat);
        const centerLon = parseFloat(lon);
        // Center a bbox of the current sizeKm on the result
        const deg = sizeKm / 111.0;
        const lonAdj = deg / Math.cos((centerLat * Math.PI) / 180);
        const newBbox = {
          minLat: centerLat - deg / 2,
          maxLat: centerLat + deg / 2,
          minLon: centerLon - lonAdj / 2,
          maxLon: centerLon + lonAdj / 2,
        };
        // Draw rect and fly map directly (don't rely on prop→effect round-trip)
        if (leafletMap.current && window.L) {
          if (rectLayer.current) leafletMap.current.removeLayer(rectLayer.current);
          const bounds = window.L.latLngBounds(
            [newBbox.minLat, newBbox.minLon],
            [newBbox.maxLat, newBbox.maxLon]
          );
          rectLayer.current = window.L.rectangle(bounds, {
            color: '#f97316', weight: 2, fillOpacity: 0.15,
          }).addTo(leafletMap.current);
          leafletMap.current.fitBounds(bounds, { padding: [40, 40] });
        }
        onBBoxChange(newBbox);
      } else {
        alert(`Kein Ergebnis fuer "${q}" gefunden.`);
      }
    } catch (_) {}
  }, [sizeKm, onBBoxChange]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 6 }}>
        <input
          name="q"
          placeholder="Ort suchen, z.B. Berlin Mitte..."
          style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #374151', background: '#1f2937', color: '#f9fafb' }}
        />
        <button type="submit" className="btn btn-secondary btn-sm">Suchen</button>
      </form>
      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
        Shift + Maus ziehen um Gebiet auszuwaehlen
      </div>
      <div ref={mapRef} style={{ height: 400, borderRadius: 8, border: '1px solid #374151' }} />
    </div>
  );
}
